import { useState, useEffect, useRef } from "react";
import { getLabelConfig } from "../../config/labelConfig";

const ImageCarousel = ({ images }) => {
  const label = getLabelConfig();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(null);
  const timeoutRef = useRef(null);

  const defaultImages = [label.banner, label.banner];
  const carouselImages = images || defaultImages;

  useEffect(() => {
    if (!carouselImages.length) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setAspectRatio(`${img.naturalWidth}/${img.naturalHeight}`);
      }
    };
    img.src = carouselImages[0];
  }, [carouselImages[0]]);

  useEffect(() => {
    resetTimeout();
    timeoutRef.current = setTimeout(() => {
      handleNext();
    }, 6000);
    return () => resetTimeout();
  }, [currentIndex]);

  const resetTimeout = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === carouselImages.length - 1 ? 0 : prev + 1,
    );
  };

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? carouselImages.length - 1 : prev - 1,
    );
  };

  const goToSlide = (index) => setCurrentIndex(index);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) handleNext();
    if (distance < -50) handlePrev();
    setTouchStart(0);
    setTouchEnd(0);
    setIsDragging(false);
  };

  const handleMouseDown = (e) => {
    setTouchStart(e.clientX);
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (isDragging) setTouchEnd(e.clientX);
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    const distance = touchStart - touchEnd;
    if (distance > 50) handleNext();
    if (distance < -50) handlePrev();
    setTouchStart(0);
    setTouchEnd(0);
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    if (isDragging) handleMouseUp();
  };

  return (
    <div className="w-full mx-auto">
      <div
        className="relative w-full overflow-hidden bg-transparent rounded-2xl"
        style={{
          aspectRatio: aspectRatio || "3/1",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        <div
          className="flex h-full transition-transform duration-500 ease-out"
          style={{
            transform: `translateX(-${currentIndex * 100}%)`,
            cursor: isDragging ? "grabbing" : "grab",
          }}
        >
          {carouselImages &&
            carouselImages?.map((image, index) => (
              <div
                key={index}
                className="min-w-full h-full flex-shrink-0 overflow-hidden rounded-2xl bg-transparent"
                style={{ userSelect: "none" }}
              >
                <img
                  src={image}
                  alt={`Slide ${index + 1}`}
                  className="w-full h-full object-cover rounded-2xl"
                  draggable="false"
                  style={{ pointerEvents: "none" }}
                  onLoad={(e) => {
                    if (index === 0) {
                      const { naturalWidth, naturalHeight } = e.currentTarget;
                      if (naturalWidth && naturalHeight) {
                        setAspectRatio(`${naturalWidth}/${naturalHeight}`);
                      }
                    }
                  }}
                />
              </div>
            ))}
        </div>

        <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-2 z-10">
          {carouselImages &&
            carouselImages?.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  currentIndex === index
                    ? "bg-secondary w-3"
                    : "bg-gray-500 hover:bg-gray-400 w-1.5"
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
        </div>
      </div>
    </div>
  );
};

export default ImageCarousel;
